import { ApiResult, httpRequest, Urls } from './Shared';
import * as moment from 'moment';
import { momentDateFormat } from '../utilities/formatting';
import { IdVerificationTypes } from './IdVerification';

const PROPERTY_URL = `${Urls.BASE_URL}/Properties`;
const PROPERTY_INTEGRATIONS = `${Urls.BASE_URL}/PropertyIntegrations`;
const ANALYTICS_URL = `${Urls.BASE_URL}/analytics`;
const TIME_SLOTS_URL = '/Timeslots';
const INTEGRATION_TYPES_URL = '/GetIntegrationTypes';
const UNIT_TYPE_URL = '/UnitTypes';
const SYNC_PROPERTY_URL = 'syncpropertyunitswithpms';
const UPDATE_AVAILABILITY = 'updateavailabilityfrompms';

export interface TourInterval {
    toursEnabled: boolean;
    startTime: string;
    endTime: string;
    blockedTimeEnabled: boolean;
    blockedStartTime: string;
    blockedEndTime: string;
    propertyHoursOpen: boolean;
    propertyHoursStartTime: string;
    propertyHoursEndTime: string;
}

export interface DayConfiguration {
    isTouringEnabledToday: boolean;
    maxTourCount: number;
    tourLength: number;
    postTourDowntime: number;
    tour24: TourInterval;
}

export interface PropertyConfiguration {
    monday: DayConfiguration;
    tuesday: DayConfiguration;
    wednesday: DayConfiguration;
    thursday: DayConfiguration;
    friday: DayConfiguration;
    saturday: DayConfiguration;
    sunday: DayConfiguration;
}

export interface TimeZone {
    timeZoneId: string;
    displayName: string;
}

export interface LeasForwardingType {
    id: number;
    name: string;
}

export interface UnitManagementType {
    id: number;
    name: string;
    beaconUuid: string;
}

export interface PropertyInfo {
    id: number;
    name: string;
    contactEmail: string;
    contactPhone: string;
    latitude?: number;
    longitude?: number;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    companyId: number;
    propertyImage?: string;
    termsLink?: string;
    mapLink?: string;
    tourPageImageURL?: string;
    requestPetInformation: boolean;
    enableUnitManagement?: boolean;
    syncLeaseStatus?: boolean;
    updateAvailableToTour?: boolean;
    syncPropertyUnitsAvailability?: boolean;
    petPolicy?: string;
    requestDesiredLeaseLength: boolean;
    requestCreditCard: boolean;
    timeZoneId: string;
    leasingUrl: string;
    goLiveDate: string;
    startDay: number;
    prospectMessagesSuppressions: number;
    pricingAndAvailabilityLink?: string;
    salesforcePropertyId?: string;
    maxTourScheduleCountsPerDay?: number;
    sendNoShowMessagesToProperty: boolean;
    hideFromSearches: boolean;
    isMobileRedirect: boolean;
    beaconUuid: string;
    leadForwardingId?: number;
    unitManagementId?: number;
    leadForwardingTypes?: LeasForwardingType[];
    unitManagementTypes?: UnitManagementType[];
    warmLeadsStatus: boolean;
    applicationWidgetStatus: boolean;
}

export interface PropertyUpdate extends PropertyInfo {
    propertyConfiguration: PropertyConfiguration | null;
}

export interface PropertyCreate extends PropertyUpdate {
    slug: string;
}

export interface Property extends PropertyCreate {
    id: number;
    unitTypes: UnitType[];
    propertyConfiguration: PropertyConfiguration;
    tour24ToursAvailable: boolean;
    virtualToursAvailable: boolean;
    usesBrivo: boolean;
    cancellationDate: string;
    idVerificationType: IdVerificationTypes;
    faqsAreActive: boolean;
    leaseUploadStats: LeaseUploadStats;
    isMobileRedirect: boolean;
    deepUrl: string;
    warmLeadsStatus: boolean;
}

export interface DashboardProperty {
    propertyId: number;
    propertyName: string;
    zip: string;
    companyId: number;
    companyName: string;
    city: string;
    state: string;
    goLiveDate: string;
    applicationWidgetStatus: boolean;
  }

export interface DashboardCardData {
    uniqueProspects: number;
    scheduleTours: number;
    completedTours: number;
    completedToursPercentage: number;
    applicationSubmitted: number;
    applicationSubmittedPercentage: number;
    businessHourScheduled: number;
    nonBusinessHourScheduled: number;
    businessHourScheduledPercentage: number;
    nonBusinessHourScheduledPercentage: number;
    weekDaysScheduled: number;
    weekEndScheduled: number;
    weekDaysScheduledPercentage: number;
    weekEndScheduledPercentage: number;
    tourScheduledSameDay: number;
    tourScheduledSameHour: number;
    tourScheduledSameDayPercentage: number;
    tourScheduledSameHourPercentage: number;
  }

export interface DashboardTourMonth {
    month: string,
    count: number
  }
  
export interface DashboardTourTimeByDay {
    day: string;
    hour: number;
    percentage: number;
    value: number;
  }
  
export interface DashboardTourTime {
    hour: number;
    count: number;
    percentage: number;
  }
  
export interface DashboardTourDays {
    day: string;
    count: number;
    percentage: number;
  }
  
export interface AnalyticPropertyInfo {
    dashboardCards: DashboardCardData;
    dashboardTourTImeByDay: DashboardTourTimeByDay[];
    dashboardTourTimes: DashboardTourTime[];
    dashboardTourDays: DashboardTourDays[];
  }
  

export interface UnitTypeUpdate {
    description: string;
    layout: string;
    pmsUnitId: string;
}

export interface UnitType extends UnitTypeUpdate {
    id: number;
}

export interface Event {
    startDateTime: string;
    endDateTime: string;
    isTourNow: boolean;
}

export interface ToursByMonth {
    tourCounts: MonthTours[];
}

export interface MonthTours {
    month: number;
    count: number;
}

export interface TourFrequencyWeek {
    tourFrequencyDays: TourFrequencyDay[];
}

export interface TourFrequencyDay {
    totalTours: number;
    tourFrequencyHours: TourFrequencyHour[];
}

export interface TourFrequencyHour {
    tourTime: string;
    tourCount: number;
}

export interface ContactInfo {
    firstName: string;
    lastName: string;
    email: string;
}

export enum LeaseMatchType {
    Exact = 0,
    Possible = 1,
    Unmatched = 2
}

export interface LeaseMatchResult {
    submittedData: ContactInfo;
    matchedData?: ContactInfo;
    matchType: LeaseMatchType;
}

export interface LeaseUploadConfirmation {
    acceptedCorrections: ContactInfo[];
    originalExactMatches: number;
    originalBorderlineMatches: number;
}

export interface LeaseUploadStats {
    date: string;
    exactMatches: number;
    borderlineMatches: number;
}

export interface PropertyFaqUpdate {
    questionText: string;
    answerText: string;
}

export interface updateAvailabilityWithPms {
    data: string;
    success: boolean;
    error: string;
}
export interface syncPropertyUnitsWithPms {
    data: string;
    success: boolean;
    error: string;
}

export interface PropertyFaq extends PropertyFaqUpdate {
    id: number;
}

export const getProperties = (): Promise<ApiResult<Property[]>> => {
    return httpRequest<Property[]>(`${PROPERTY_URL}`, 'get');
};

export const getJustProperties = (): Promise<ApiResult<Property[]>> => {
    return httpRequest<Property[]>(`${PROPERTY_URL}/justproperties`, 'get');
};

export const getAdminDashboardProperties = (): Promise<ApiResult<DashboardProperty[]>> => {
    return httpRequest<DashboardProperty[]>(`${ANALYTICS_URL}/properties`, 'get');
};

export const getAdminDashboardData = (filterData: any): Promise<ApiResult<AnalyticPropertyInfo>> => {
    return httpRequest<AnalyticPropertyInfo>(`${ANALYTICS_URL}/dashboard`, 'post', filterData);
};

export const getAnalyticsExportData = (info: any) =>
    httpRequest<any>(
        `${ANALYTICS_URL}/dashboardData`,
        'post',
        info
);

export const getToursByMonthForDashboard = (filterData: any): Promise<ApiResult<DashboardTourMonth[]>> => {
    return httpRequest<DashboardTourMonth[]>(`${ANALYTICS_URL}/toursbymonth`, 'post', filterData);
};

export const getPropertyInformations = (): Promise<ApiResult<Property[]>> => {
    return httpRequest<Property[]>(`${PROPERTY_URL}/information`, 'get');
};

export const getPropertyInformationByPropertySlug = (slug: string): Promise<ApiResult<Property>> => {
    return httpRequest<Property>(`${PROPERTY_URL}/information/${slug}`, 'get');
};

export const getPropertyByKey = (publicKey: string): Promise<ApiResult<Property>> => {
    return httpRequest<Property>(`${PROPERTY_URL}/tour/${publicKey}`, 'get');
};

export const getPropertyById = (id: number): Promise<ApiResult<Property>> => {
    return httpRequest<Property>(`${PROPERTY_URL}/${id}`, 'get');
};

export const activateMasterLock = (propertyId: number, identifier: any): Promise<ApiResult<updateAvailabilityWithPms>> => {
    return httpRequest<any>(
        `${PROPERTY_URL}/ActivateMasterLock/${propertyId}/${identifier}`,
        'post'
    );
};

export const getIntegrationTypes = (): Promise<ApiResult<Event[]>> => {
    return httpRequest<Event[]>(
        `${PROPERTY_URL}${INTEGRATION_TYPES_URL}`,
        'get'
    );
};
export const syncPropertyUnitsWithPms = (propertyId: number): Promise<ApiResult<syncPropertyUnitsWithPms>> => {
    return httpRequest<any>(
        `${PROPERTY_URL}/${propertyId}/${SYNC_PROPERTY_URL}`,
        'post'
    );
};
export const updateAvailabilityWithPms = (propertyId: number): Promise<ApiResult<updateAvailabilityWithPms>> => {
    return httpRequest<any>(
        `${PROPERTY_INTEGRATIONS}/${propertyId}/${UPDATE_AVAILABILITY}`,
        'post'
    );
};

export const getAvailableTimeSlots = (
    propertyId: number,
    date: string
): Promise<ApiResult<Event[]>> => {
    return httpRequest<Event[]>(
        `${PROPERTY_URL}${TIME_SLOTS_URL}/${propertyId}&date=${date}`,
        'get'
    );
};

export const updateProperty = (
    id: number,
    propertyUpdate: PropertyUpdate
): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(`${PROPERTY_URL}/${id}`, 'put', propertyUpdate);
};

export const createProperty = (newProperty: PropertyCreate): Promise<ApiResult<Property>> => {
    return httpRequest<Property>(`${PROPERTY_URL}`, 'post', newProperty);
};

export const deleteProperty = (id: number): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(`${PROPERTY_URL}/${id}`, 'delete');
};

export const updatePropertyConfiguration = (
    propertyId: number,
    propertyConfiguration: PropertyConfiguration
): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(
        `${PROPERTY_URL}/${propertyId}/PropertyConfiguration`,
        'put',
        propertyConfiguration
    );
};

export const getUnitTypes = (propertyId: number): Promise<ApiResult<UnitType[]>> => {
    return httpRequest<UnitType[]>(`${PROPERTY_URL}/${propertyId}${UNIT_TYPE_URL}`, 'get');
};

export const deleteUnitType = (
    propertyId: number,
    unitTypeId: number
): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(
        `${PROPERTY_URL}/${propertyId}${UNIT_TYPE_URL}/${unitTypeId}`,
        'delete'
    );
};

export const updateUnitType = (
    propertyId: number,
    unitTypeId: number,
    unitType: UnitTypeUpdate
): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(
        `${PROPERTY_URL}/${propertyId}${UNIT_TYPE_URL}/${unitTypeId}`,
        'put',
        unitType
    );
};

export const createUnitType = (
    propertyId: number,
    unitType: UnitTypeUpdate
): Promise<ApiResult<UnitType>> => {
    console.log(unitType, 'unitType')
    return httpRequest<UnitType>(`${PROPERTY_URL}/${propertyId}${UNIT_TYPE_URL}`, 'post', unitType);
};

export const getAvailableTimeZones = () => httpRequest<TimeZone[]>(`${PROPERTY_URL}/timezones`);

export const getToursByMonth = (propertyId: number) =>
    httpRequest<ToursByMonth>(`${PROPERTY_URL}/${propertyId}/ToursByMonth`, 'get');

export const getNumberOfTours = (
    propertyId: number,
    startDate: moment.Moment,
    endDate: moment.Moment
) =>
    httpRequest<Number>(
        `${PROPERTY_URL}/ToursCount?propertyId=${propertyId}` +
        `&startDate=${startDate.format(momentDateFormat)}` +
        `&endDate=${endDate.format(momentDateFormat)}`
    );

export const getTourFrequencyForProperty = (propertyId: number) =>
    httpRequest<TourFrequencyWeek>(`${PROPERTY_URL}/${propertyId}/TourFrequency`);

export const getPropertyAuditLog = (): Promise<ApiResult<string>> => {
    return httpRequest<string>(`${PROPERTY_URL}/PropertyAuditLog`, 'get');
};

export const matchLeases = (propertyId: number, data: FormData) =>
    httpRequest<LeaseMatchResult[]>(
        `${PROPERTY_URL}/${propertyId}/MatchLeases`,
        'post',
        data,
        true
    );

export const confirmLeaseUpload = (propertyId: number, data: LeaseUploadConfirmation) =>
    httpRequest<boolean>(`${PROPERTY_URL}/${propertyId}/ConfirmLeaseUpload`, 'post', data);

export const migrateProperty = (
    propertyId: number,
    destinationCompanyId: number
): Promise<ApiResult<undefined>> => {
    return httpRequest<undefined>(
        `${PROPERTY_URL}/${propertyId}/migrate`,
        'post',
        destinationCompanyId
    );
};



// WEBPACK FOOTER //
// ./src/api/Property.ts